import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, SlidersHorizontal, ArrowUpDown, X, ShoppingBag, Star, Filter, ChevronDown, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';
import { useCart } from '../hooks/useCart';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_PRODUCTS = [
  // Biryani & Rice
  { id: 'p1', name: 'Chicken Biryani', category: 'Biryani & Rice', subcategory: 'Biryani', price: 199, brand: "Nistala's Kitchen", description: 'Aromatic long-grain rice layered with tender chicken, saffron & spices. 550g meal.', image: 'https://images.unsplash.com/photo-1727404539546-272b2504daab?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.8, bestseller: true },
  { id: 'p2', name: 'Mutton Biryani', category: 'Biryani & Rice', subcategory: 'Biryani', price: 299, brand: "Nistala's Kitchen", description: 'Premium mutton pieces slow-cooked with basmati rice & whole spices. 550g meal.', image: 'https://images.unsplash.com/photo-1727404746799-253aa9a8ace6?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.9, bestseller: true },
  { id: 'p3', name: 'Veg Biryani', category: 'Biryani & Rice', subcategory: 'Biryani', price: 149, brand: "Nistala's Kitchen", description: 'Mixed vegetables with fragrant rice, mint & fried onions. 550g meal.', image: 'https://images.unsplash.com/photo-1752673508949-f4aeeaef75f0?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.5 },
  { id: 'p4', name: 'Egg Fried Rice', category: 'Biryani & Rice', subcategory: 'Rice Bowl', price: 129, brand: "Nistala's Kitchen", description: 'Fluffy egg fried rice with fresh vegetables & soy seasoning.', image: 'https://images.unsplash.com/photo-1771796992969-05bb88a0516a?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.3 },
  { id: 'p5', name: 'Chicken Rice Bowl', category: 'Biryani & Rice', subcategory: 'Rice Bowl', price: 169, brand: "Nistala's Kitchen", description: 'Grilled chicken over steamed rice with gravy & fresh salad.', image: 'https://images.unsplash.com/photo-1727404539546-272b2504daab?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.4 },

  // Daily Meals
  { id: 'p6', name: 'Veg Thali', category: 'Daily Meals', subcategory: 'Thali', price: 119, brand: "Nistala's Kitchen", description: 'Complete meal — dal, rice, 2 sabzi, roti, salad & papad.', image: 'https://images.unsplash.com/photo-1633975573517-653b02dcae4f?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.6 },
  { id: 'p7', name: 'Non-Veg Thali', category: 'Daily Meals', subcategory: 'Thali', price: 179, brand: "Nistala's Kitchen", description: 'Dal, rice, chicken curry, roti, salad & dessert.', image: 'https://images.unsplash.com/photo-1581891436344-e5c03ac74cb6?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.7 },
  { id: 'p8', name: 'Roti + Sabzi Combo', category: 'Daily Meals', subcategory: 'Combo', price: 89, brand: "Nistala's Kitchen", description: '4 rotis with seasonal sabzi — simple, wholesome, homely.', image: 'https://images.unsplash.com/photo-1656422046523-469409b2b4d0?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.2 },
  { id: 'p9', name: 'Dal Rice Combo', category: 'Daily Meals', subcategory: 'Combo', price: 99, brand: "Nistala's Kitchen", description: 'Homestyle dal tadka with steamed rice & pickle.', image: 'https://images.unsplash.com/photo-1752673508949-f4aeeaef75f0?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.3 },

  // Organic Products
  { id: 'p10', name: 'Organic Turmeric Powder', category: 'Organic Products', subcategory: 'Spices', price: 149, brand: 'Organic Hues', description: 'Pure, chemical-free turmeric from Telangana farms. 200g pack.', image: 'https://images.unsplash.com/photo-1656422046523-469409b2b4d0?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.8 },
  { id: 'p11', name: 'Farm Fresh Honey', category: 'Organic Products', subcategory: 'Natural', price: 349, brand: 'Organic Hues', description: 'Raw, unprocessed honey from forest beekeepers. 500ml jar.', image: 'https://images.unsplash.com/photo-1656422046523-469409b2b4d0?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.9, bestseller: true },
  { id: 'p12', name: 'Cold-Pressed Groundnut Oil', category: 'Organic Products', subcategory: 'Oils', price: 299, brand: 'Organic Hues', description: 'Traditional wood-pressed oil, chemical-free. 1L bottle.', image: 'https://images.unsplash.com/photo-1656422046523-469409b2b4d0?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.7 },
  { id: 'p13', name: 'Red Chilli Powder', category: 'Organic Products', subcategory: 'Spices', price: 129, brand: 'Organic Hues', description: 'Guntur chillies, sun-dried & stone-ground. 200g pack.', image: 'https://images.unsplash.com/photo-1656422046523-469409b2b4d0?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.5 },

  // Snacks & Sweets
  { id: 'p14', name: 'Mixture Namkeen', category: 'Snacks & Sweets', subcategory: 'Namkeen', price: 99, brand: "Rise & Rolls", description: 'Crunchy South Indian mixture — perfect tea-time snack. 250g.', image: 'https://images.unsplash.com/photo-1772457677641-394bbc0c20f6?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.4 },
  { id: 'p15', name: 'Murukku', category: 'Snacks & Sweets', subcategory: 'Namkeen', price: 119, brand: "Rise & Rolls", description: 'Crispy rice flour spirals with cumin & sesame. 200g.', image: 'https://images.pexels.com/photos/13220344/pexels-photo-13220344.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', rating: 4.6 },
  { id: 'p16', name: 'Laddu Box', category: 'Snacks & Sweets', subcategory: 'Sweets', price: 249, brand: "Rise & Rolls", description: 'Boondi laddu made with pure ghee. Box of 12.', image: 'https://images.pexels.com/photos/36236282/pexels-photo-36236282.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', rating: 4.8 },

  // Bulk & Catering
  { id: 'p17', name: 'Party Biryani (5kg)', category: 'Bulk & Catering', subcategory: 'Party Pack', price: 1499, brand: "Nistala's Kitchen", description: 'Serves 15-20 people. Chicken biryani with sides.', image: 'https://images.unsplash.com/photo-1727404539546-272b2504daab?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.9 },
  { id: 'p18', name: 'Corporate Lunch Box (50)', category: 'Bulk & Catering', subcategory: 'Corporate', price: 4999, brand: "Nistala's Kitchen", description: '50 individual lunch boxes — biryani + sides. Next-day delivery.', image: 'https://images.unsplash.com/photo-1666479258732-5ea17469b610?crop=entropy&cs=srgb&fm=jpg&q=85', rating: 4.7 },
];

const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'name-az', label: 'Name: A to Z' },
  { value: 'rating', label: 'Top Rated' },
];

const Products = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeSubcategory, setActiveSubcategory] = useState('All');
  const [sortBy, setSortBy] = useState('popular');
  const [showFilters, setShowFilters] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [addedId, setAddedId] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    fetch(`${API}/api/products`).then(r => r.json()).then(d => {
      setProducts(d.length > 0 ? d : DEFAULT_PRODUCTS);
    }).catch(() => setProducts(DEFAULT_PRODUCTS));
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(products.map(p => p.category))];
    return ['All', ...cats];
  }, [products]);

  const subcategories = useMemo(() => {
    const filtered = activeCategory === 'All' ? products : products.filter(p => p.category === activeCategory);
    const subs = [...new Set(filtered.map(p => p.subcategory).filter(Boolean))];
    return ['All', ...subs];
  }, [products, activeCategory]);

  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q));
    }
    if (activeCategory !== 'All') result = result.filter(p => p.category === activeCategory);
    if (activeSubcategory !== 'All') result = result.filter(p => p.subcategory === activeSubcategory);

    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name-az': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'rating': result.sort((a, b) => (b.rating || 0) - (a.rating || 0)); break;
      default: result.sort((a, b) => (b.bestseller ? 1 : 0) - (a.bestseller ? 1 : 0)); break;
    }
    return result;
  }, [products, search, activeCategory, activeSubcategory, sortBy]);

  const resetFilters = () => { setActiveCategory('All'); setActiveSubcategory('All'); setSearch(''); setSortBy('popular'); };

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="products-page">
      <SEO title="Products" description="Explore Nistala's range of organic foods, fresh meals, biryanis, snacks, and bulk catering. Farm-fresh, chemical-free products delivered to your door." />

      {/* Hero */}
      <section className="relative py-16 overflow-hidden bg-[#2C1810]">
        <div className="absolute inset-0 opacity-15">
          <div className="w-full h-full" style={{ background: 'radial-gradient(circle at 20% 50%, #D4A574 0%, transparent 50%), radial-gradient(circle at 80% 50%, #5C7A3A 0%, transparent 50%)' }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 text-center pt-12 pb-4">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="inline-block text-xs tracking-[0.25em] uppercase text-[#D4A574] font-semibold mb-3">
            Fresh from Farm & Kitchen
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-4xl sm:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Our <span className="text-[#D4A574]">Products</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="text-sm text-white/60 max-w-lg mx-auto mb-6">
            Meals, organic products, snacks & bulk orders — all sourced directly from farms.
          </motion.p>

          {/* Search Bar */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="max-w-md mx-auto relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7a6555]" />
            <Input
              placeholder="Search products, meals, brands..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-11 pr-4 py-3 bg-white/95 backdrop-blur-sm border-0 rounded-xl text-sm text-[#2C1810] shadow-lg focus:ring-2 focus:ring-[#D4A574]/40 h-12"
              data-testid="product-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7a6555] hover:text-[#2C1810] cursor-pointer">
                <X size={16} />
              </button>
            )}
          </motion.div>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4">

          {/* Category Tabs */}
          <div className="mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide" data-testid="category-tabs">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(cat); setActiveSubcategory('All'); }}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-[#2C1810] text-white shadow-md'
                      : 'bg-white text-[#7a6555] border border-[#E8DFD3] hover:border-[#D4A574] hover:text-[#2C1810]'
                  }`}
                  data-testid={`cat-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {cat}
                  {cat !== 'All' && (
                    <span className={`ml-1.5 text-xs ${activeCategory === cat ? 'text-white/60' : 'text-[#7a6555]/50'}`}>
                      {products.filter(p => p.category === cat).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Subcategory + Sort Row */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-2 overflow-x-auto">
              {subcategories.length > 2 && subcategories.map(sub => (
                <button
                  key={sub}
                  onClick={() => setActiveSubcategory(sub)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                    activeSubcategory === sub
                      ? 'bg-[#5C7A3A] text-white'
                      : 'bg-white text-[#7a6555] border border-[#E8DFD3] hover:border-[#5C7A3A]/40'
                  }`}
                  data-testid={`subcat-${sub.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {sub}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-[#7a6555]">{filteredProducts.length} products</span>

              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSort(!showSort)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-white border border-[#E8DFD3] text-[#7a6555] hover:border-[#D4A574] cursor-pointer transition-colors"
                  data-testid="sort-btn"
                >
                  <ArrowUpDown size={14} /> Sort
                  <ChevronDown size={12} className={`transition-transform ${showSort ? 'rotate-180' : ''}`} />
                </button>
                {showSort && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-xl border border-[#E8DFD3] py-1.5 z-30" data-testid="sort-dropdown">
                    {SORT_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => { setSortBy(opt.value); setShowSort(false); }}
                        className={`w-full text-left px-4 py-2 text-xs transition-colors cursor-pointer ${sortBy === opt.value ? 'text-[#5C7A3A] bg-[#5C7A3A]/5 font-semibold' : 'text-[#7a6555] hover:bg-[#FDF8F0]'}`}
                        data-testid={`sort-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Reset */}
              {(activeCategory !== 'All' || activeSubcategory !== 'All' || search || sortBy !== 'popular') && (
                <button onClick={resetFilters} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium bg-red-50 text-red-500 hover:bg-red-100 cursor-pointer transition-colors" data-testid="reset-filters">
                  <X size={12} /> Reset
                </button>
              )}
            </div>
          </div>

          {/* Product Grid */}
          <AnimatePresence mode="wait">
            {filteredProducts.length === 0 ? (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-16" data-testid="no-products">
                <ShoppingBag size={48} className="mx-auto mb-4 text-[#D4A574]/40" />
                <h3 className="text-lg font-bold text-[#2C1810] mb-1">No products found</h3>
                <p className="text-sm text-[#7a6555] mb-4">Try adjusting your search or filters</p>
                <Button onClick={resetFilters} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white text-sm cursor-pointer">Clear Filters</Button>
              </motion.div>
            ) : (
              <motion.div
                key={`${activeCategory}-${activeSubcategory}-${sortBy}-${search}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                data-testid="product-grid"
              >
                {filteredProducts.map((product, idx) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(idx * 0.04, 0.4), duration: 0.35 }}
                  >
                    <Card className="group h-full bg-white border border-[#E8DFD3] overflow-hidden hover:shadow-xl hover:shadow-[#D4A574]/10 transition-all duration-400 hover:-translate-y-1" data-testid={`product-card-${product.id}`}>
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-600"
                          loading="lazy"
                        />
                        {/* Badges */}
                        <div className="absolute top-2 left-2 flex flex-col gap-1">
                          {product.bestseller && (
                            <span className="bg-[#C4844A] text-white text-[9px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Star size={9} fill="white" /> Best Seller
                            </span>
                          )}
                          <span className="bg-[#2C1810]/75 backdrop-blur-sm text-white text-[9px] font-medium px-2 py-0.5 rounded-full">
                            {product.subcategory || product.category}
                          </span>
                        </div>
                        {/* Quick Add */}
                        <div className="absolute bottom-0 inset-x-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                          <button
                            onClick={() => {
                              addItem(product);
                              setAddedId(product.id);
                              setTimeout(() => setAddedId(null), 1200);
                            }}
                            className="w-full bg-[#5C7A3A] text-white text-xs font-semibold py-2.5 flex items-center justify-center gap-1.5 cursor-pointer hover:bg-[#4a6530]"
                            data-testid={`add-to-cart-${product.id}`}
                          >
                            {addedId === product.id ? (
                              <><Check size={13} /> Added!</>
                            ) : (
                              <><ShoppingBag size={13} /> Add to Cart</>
                            )}
                          </button>
                        </div>
                      </div>
                      <CardContent className="p-3.5">
                        <p className="text-[10px] font-semibold text-[#C4844A] tracking-wide uppercase mb-0.5">{product.brand}</p>
                        <h3 className="text-sm font-bold text-[#2C1810] mb-1 leading-snug group-hover:text-[#5C7A3A] transition-colors">{product.name}</h3>
                        <p className="text-[11px] text-[#7a6555] leading-relaxed mb-2 line-clamp-2">{product.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-[#2C1810]">
                            <span className="text-xs font-normal text-[#7a6555]">₹</span>{product.price}
                          </span>
                          {product.rating && (
                            <span className="flex items-center gap-0.5 text-xs text-[#C4844A] font-medium">
                              <Star size={11} fill="#C4844A" strokeWidth={0} /> {product.rating}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-10 bg-[#2C1810]" data-testid="products-cta">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Need Something <span className="text-[#D4A574]">Custom?</span>
          </h2>
          <p className="text-sm text-white/60 mb-5">Bulk orders, corporate meals, or custom catering — we've got you covered.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold px-6 cursor-pointer" data-testid="products-cta-bulk">
              Bulk Order Inquiry
            </Button>
            <a href="https://wa.me/91XXXXXXXXXX" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold px-6 cursor-pointer" data-testid="products-cta-whatsapp">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
